/**
 * Сборка адреса одной строкой из составных полей.
 *
 * Формы приказа МЭР № 530 требуют адрес по частям (субъект, район, город,
 * улица, дом), а заявление печатает его одной строкой. Заполнять и то и другое
 * вручную — двойная работа, поэтому строка собирается из частей.
 *
 * Правка строки вручную имеет приоритет: юрист может привести её к виду,
 * который стоит в паспорте, и эта правка не теряется (признак `fullManual`).
 */

import type { Address } from '../types/common.js';

/** Порядок частей — как в адресе на конверте. */
const PARTS: Array<keyof Address> = [
  'postalCode',
  'region',
  'area',
  'city',
  'settlement',
  'street',
  'house',
  'block',
  'flat',
];

export function composeAddress(address: Address): string {
  return PARTS.map((key) => (address[key] as string | undefined)?.trim())
    .filter((value): value is string => Boolean(value))
    .join(', ');
}

/**
 * Изменение составной части адреса с пересборкой строки.
 * Строка не трогается, если её правили вручную.
 */
export function updateAddressPart(address: Address, key: keyof Address, value: string): Address {
  const next: Address = { ...address, [key]: value, source: 'manual' };
  if (next.fullManual) return next;
  return { ...next, full: composeAddress(next) };
}

/**
 * Адрес одной строкой → составные поля формы (25.10.2026, загрузка запроса
 * кредитору). Части распознаются по своим словам: «город Биробиджан»,
 * «улица Южная», «дом 54», «р-н …», «кв. 1»; шесть цифр — индекс. Первая
 * часть, похожая на субъект («область», «край», «республика», «автономн…»),
 * — субъект; его наименование сверяет со справочником уже интерфейс.
 * Что не распознано — в поле не попадает, но остаётся в строке `full`.
 */
export function parseAddressString(full: string): Address {
  const address: Address = { full: full.trim() };
  for (const raw of full.split(',')) {
    const part = raw.trim();
    if (!part) continue;
    if (/^\d{6}$/.test(part)) address.postalCode ??= part;
    else if (!address.region && /(область|обл\.?$|край|республика|автономн|^г\.?\s*(москва|санкт-петербург|севастополь)$)/i.test(part)) address.region = part;
    else if (/(^|\s)(район|р-н)(\s|$)/i.test(part)) address.area ??= part;
    else if (/^(город|г\.)\s*\S/i.test(part)) address.city ??= part.replace(/^город\s+/i, 'г. ');
    else if (/^(село|с\.|деревня|д\.\s*[А-ЯЁ]|посёлок|поселок|пос\.|п\.|станица|ст-ца|рабочий посёлок|рп\.?|пгт)\s*/i.test(part)) address.settlement ??= part;
    else if (/^(улица|ул\.|проспект|пр-т|пр-кт|переулок|пер\.|бульвар|б-р|шоссе|ш\.|проезд|пр-д|площадь|пл\.|набережная|наб\.|тупик|микрорайон|мкр\.?)\s*/i.test(part) || /\s(ул|улица)\.?$/i.test(part)) address.street ??= part;
    else if (/^(дом|д\.)\s*\S/i.test(part)) address.house ??= part.replace(/^дом\s+/i, 'д. ');
    else if (/^(корпус|корп\.|к\.|строение|стр\.)\s*\S/i.test(part)) address.block ??= part;
    else if (/^(квартира|кв\.|офис|оф\.|помещение|помещ\.)\s*\S/i.test(part)) address.flat ??= part.replace(/^квартира\s+/i, 'кв. ');
  }
  return address;
}

/** Ручная правка строки адреса: дальнейшая пересборка отключается. */
export function setAddressFull(address: Address, full: string): Address {
  return { ...address, full, fullManual: true, source: 'manual' };
}

/** Адрес заполнен настолько, что его можно печатать в документе. */
export function isAddressUsable(address: Address): boolean {
  return Boolean(address.full?.trim()) || Boolean(address.region?.trim() && address.house?.trim());
}

/*
  Единый стиль адреса кредитора (сверка «Айнбиндер», 26.10.2026): адреса
  приходят из справочника («г. Москва, ул. Вавилова, д. 19»), из DaData
  («г Москва, р-н Куркино, ул Соколово-Мещерская, д 25») и из ЕГРЮЛ
  ЗАГЛАВНЫМИ («Г.САНКТ-ПЕТЕРБУРГ, ВН.ТЕР.Г. МУНИЦИПАЛЬНЫЙ ОКРУГ …»).
  Приводятся к стилю справочника при внесении в дело (решение заказчика):
  сокращения с точкой, строчные буквы вместо заглавных, без страны,
  внутригородских районов и муниципальных округов.
*/
const ABBREVIATIONS = [
  'г', 'ул', 'д', 'стр', 'к', 'корп', 'пом', 'помещ', 'оф', 'лит', 'обл', 'пр-кт', 'просп', 'пер', 'наб', 'ш', 'б-р',
  'зд', 'кв', 'эт', 'ком', 'мкр', 'пл', 'пгт', 'рп', 'с', 'п', 'тер', 'туп', 'проезд', 'влд', 'соор',
];
/** Слова, которые в адресе пишутся со строчной буквы */
const LOWER_WORDS = new Set([
  'область', 'край', 'район', 'улица', 'проспект', 'переулок', 'шоссе', 'бульвар', 'набережная', 'площадь', 'тупик',
  'литер', 'литера', 'офис', 'этаж', 'помещение', 'комната', 'город', 'поселок', 'посёлок', 'село', 'деревня', 'мгстр',
  'автономный', 'автономная', 'округ', 'дом', 'строение', 'корпус', 'квартира', 'здание', 'на', 'де', 'р-н',
]);
const abbreviation = new RegExp(`^(${ABBREVIATIONS.map((a) => a.replace('-', '\-')).join('|')})\.?$`, 'i');

const capitalizeWord = (word: string) =>
  word
    .split('-')
    // Первая буква, а не первый знак: «(якутия)» → «(Якутия)»
    .map((part, i) => (i > 0 && LOWER_WORDS.has(part) ? part : part.replace(/[А-ЯЁа-яёA-Za-z]/, (c) => c.toUpperCase())))
    .join('-');

export function creditorAddressStyle(value: string): string {
  const letters = value.replace(/[^А-ЯЁа-яёA-Za-z]/g, '');
  const shouting = letters.length > 0 && letters.replace(/[^А-ЯЁA-Z]/g, '').length / letters.length > 0.6;

  const parts = value
    // «Г.САНКТ-ПЕТЕРБУРГ» → «Г. САНКТ-ПЕТЕРБУРГ»
    .replace(/(^|[\s,])([А-ЯЁа-яё-]{1,6})\.(?=[А-ЯЁа-яё])/g, (whole, lead: string, word: string) => (abbreviation.test(word) ? `${lead}${word}. ` : whole))
    .split(',')
    .map((part) => part.trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .filter((part) => !/^(россия|российская федерация)$/i.test(part))
    // Внутригородская территория и муниципальный округ в адресе для суда лишние
    .filter((part) => !/^вн\.?\s*тер\.?\s*г\.?/i.test(part) && !/муниципальный округ/i.test(part))
    // Городской округ перед городом — тоже (реестр Банка России, 01.11.2026):
    // «Г.О. ГОРОД КОСТРОМА, Г. КОСТРОМА», «Городской округ город Краснодар»
    .filter((part) => !/^(г\.\s*о\.|городской округ)(\s|$)/i.test(part))
    // «Д.113», «дом 176» → «д. 113»; «СТР.9», «строение 2» → «стр. 9»;
    // «город Москва» → «г. Москва», «улица Советская» → «ул. Советская»
    .map((part) =>
      part
        .replace(/(^|\s)(?:д|дом)\.?\s*(?=\d)/gi, '$1д. ')
        .replace(/(^|\s)(?:стр|строение)\.?\s*(?=\d)/gi, '$1стр. ')
        .replace(/^город\s/i, 'г. ')
        .replace(/(^|\s)улица\s/i, '$1ул. '),
    );

  let cityFound = false;
  const styled: string[] = [];
  for (const part of parts) {
    const words = part.split(' ').map((word) => {
      if (abbreviation.test(word)) return `${word.replace(/\.$/, '').toLowerCase()}.`;
      if (!shouting || /\d/.test(word)) return word;
      const lower = word.toLowerCase();
      return LOWER_WORDS.has(lower) ? lower : capitalizeWord(lower);
    });
    const text = words.join(' ');
    // Район города («г. Москва, р-н Куркино») не нужен; район области нужен
    if (cityFound && /(^р-н\.?\s|\sр-н\.?$|\sрайон$)/i.test(text)) continue;
    if (/^г\.\s/.test(text)) cityFound = true;
    styled.push(text);
  }
  return styled.join(', ');
}
